/**
 * Manager Selection Utilities for Poker Reborn
 *
 * Pure functions that select the right manager from managers.ts
 * based on player balance, game type, and preferences.
 * No network or UI dependencies — fully testable.
 *
 * @see src/config/managers.ts - Data source
 * @see src/config/lobby.ts - Configuration (budgetFraction, defaults)
 */

import type {
  GameType,
  ManagerConfig,
  CashManagerConfig,
  FastFoldManagerConfig,
  SngManagerConfig,
  CashTier,
  SngTier,
} from '@/config/managers';
import {
  CASH_MANAGERS,
  FASTFOLD_MANAGERS,
  SNG_MANAGERS,
} from '@/config/managers';
import { PLAY_NOW_CONFIG } from '@/config/lobby';

// =============================================================================
// TYPES
// =============================================================================

/** Result of a manager auto-selection */
export interface ManagerMatch {
  /** The matched manager config */
  readonly manager: ManagerConfig;
  /** Manager name, which IS the GLID for backend communication */
  readonly managerGlid: string;
}

/** Level/tier info for Custom Play wizard display */
export interface LevelOption {
  /** Blind level number (1-12) or SNG tier index */
  readonly level: number;
  /** Display tier name (e.g., "Micro", "Bronze") */
  readonly tier: string;
  /** Human-readable label (e.g., "1/2", "Buy-in: 500") */
  readonly label: string;
  /** Available seat options for this level */
  readonly seatOptions: readonly number[];
}

// =============================================================================
// AUTO-SELECT (PLAY NOW)
// =============================================================================

/**
 * Auto-select the best manager for "Play Now" based on player balance.
 *
 * Formula: playBudget = balance * budgetFraction
 * Cash/FF: picks highest level where maxBuyin <= playBudget (full stack buy-in).
 * SNG: picks highest tier where buyin <= playBudget.
 * Falls back to the lowest level if the player can't afford any.
 *
 * @param gameType - Which game type to select from
 * @param playerBalance - Player's current chip balance
 * @param seatPreference - Preferred seat count (only relevant for cash)
 * @returns The best matching manager, or null if no managers exist
 */
export function autoSelectManager(
  gameType: GameType,
  playerBalance: number,
  seatPreference?: 6 | 9,
): ManagerMatch | null {
  const { budgetFraction, defaultSeats } = PLAY_NOW_CONFIG[gameType];
  const playBudget = playerBalance * budgetFraction;

  switch (gameType) {
    case 'cash':
      return autoSelectCashManager(playBudget, seatPreference ?? defaultSeats);
    case 'fastfold':
      return autoSelectFastFoldManager(playBudget);
    case 'sng':
      return autoSelectSngManager(playBudget);
  }
}

function autoSelectCashManager(
  playBudget: number,
  seats: 6 | 9,
): ManagerMatch | null {
  const candidatesForSeats = CASH_MANAGERS.filter(
    (manager) => manager.seats === seats,
  );

  if (candidatesForSeats.length === 0) return null;

  // Find highest level where player can afford maxBuyin (full stack)
  const affordable = candidatesForSeats.filter(
    (manager) => manager.maxBuyin <= playBudget,
  );

  // Pick highest affordable, or fall back to lowest available
  const bestMatch = affordable.length > 0
    ? affordable[affordable.length - 1] // sorted ascending by level in managers.ts
    : candidatesForSeats[0];

  if (!bestMatch) return null;
  return { manager: bestMatch, managerGlid: bestMatch.name };
}

function autoSelectFastFoldManager(
  playBudget: number,
): ManagerMatch | null {
  if (FASTFOLD_MANAGERS.length === 0) return null;

  // Find highest level where player can afford maxBuyin (full stack)
  const affordable = FASTFOLD_MANAGERS.filter(
    (manager) => manager.maxBuyin <= playBudget,
  );

  const bestMatch = affordable.length > 0
    ? affordable[affordable.length - 1]
    : FASTFOLD_MANAGERS[0];

  if (!bestMatch) return null;
  return { manager: bestMatch, managerGlid: bestMatch.name };
}

function autoSelectSngManager(
  playBudget: number,
): ManagerMatch | null {
  if (SNG_MANAGERS.length === 0) return null;

  // Find highest tier where player can afford the buyin
  const affordable = SNG_MANAGERS.filter(
    (manager) => manager.buyin <= playBudget,
  );

  const bestMatch = affordable.length > 0
    ? affordable[affordable.length - 1]
    : SNG_MANAGERS[0];

  if (!bestMatch) return null;
  return { manager: bestMatch, managerGlid: bestMatch.name };
}

// =============================================================================
// SPECIFIC MANAGER LOOKUP (CUSTOM PLAY)
// =============================================================================

/**
 * Find a specific cash manager by blind level and seat count.
 * @param level - Blind level (1-12)
 * @param seats - Seat count (6 or 9)
 */
export function findCashManager(
  level: number,
  seats: 6 | 9,
): CashManagerConfig | undefined {
  return CASH_MANAGERS.find(
    (manager) => manager.level === level && manager.seats === seats,
  );
}

/**
 * Find a specific fast-fold manager by blind level.
 * Fast-fold is always 6-max.
 * @param level - Blind level (1-12)
 */
export function findFastFoldManager(
  level: number,
): FastFoldManagerConfig | undefined {
  return FASTFOLD_MANAGERS.find(
    (manager) => manager.level === level,
  );
}

/**
 * Find a specific SNG manager by tier name.
 * SNG is always 9-max.
 * @param tier - SNG tier (e.g., "Bronze", "Silver")
 */
export function findSngManager(
  tier: SngTier,
): SngManagerConfig | undefined {
  return SNG_MANAGERS.find(
    (manager) => manager.tier === tier,
  );
}

// =============================================================================
// AVAILABLE OPTIONS (FOR CUSTOM PLAY WIZARD UI)
// =============================================================================

/**
 * Get available levels/tiers for a game type, for the Custom Play wizard.
 * Returns display-friendly data for rendering the selection list.
 */
export function getAvailableLevels(gameType: GameType): readonly LevelOption[] {
  switch (gameType) {
    case 'cash':
      return getCashLevels();
    case 'fastfold':
      return getFastFoldLevels();
    case 'sng':
      return getSngLevels();
  }
}

function getCashLevels(): readonly LevelOption[] {
  // Group cash managers by level to show one row per blind level
  const levelMap = new Map<number, { tier: CashTier; smallBlind: number; bigBlind: number; seatOptions: number[] }>();

  for (const manager of CASH_MANAGERS) {
    const existing = levelMap.get(manager.level);
    if (existing) {
      existing.seatOptions.push(manager.seats);
    } else {
      levelMap.set(manager.level, {
        tier: manager.tier,
        smallBlind: manager.smallBlind,
        bigBlind: manager.bigBlind,
        seatOptions: [manager.seats],
      });
    }
  }

  const levels: LevelOption[] = [];
  for (const [level, data] of levelMap) {
    levels.push({
      level,
      tier: data.tier,
      label: `${formatChips(data.smallBlind)}/${formatChips(data.bigBlind)}`,
      seatOptions: data.seatOptions.sort((a, b) => a - b),
    });
  }

  return levels.sort((a, b) => a.level - b.level);
}

function getFastFoldLevels(): readonly LevelOption[] {
  return FASTFOLD_MANAGERS.map((manager) => ({
    level: manager.level,
    tier: manager.tier,
    label: `${formatChips(manager.smallBlind)}/${formatChips(manager.bigBlind)}`,
    seatOptions: [6] as readonly number[],
  }));
}

function getSngLevels(): readonly LevelOption[] {
  return SNG_MANAGERS.map((manager, index) => ({
    level: index + 1,
    tier: manager.tier,
    label: `Buy-in: ${formatChips(manager.buyin)}`,
    seatOptions: [9] as readonly number[],
  }));
}

// =============================================================================
// FORMATTING HELPERS
// =============================================================================

/**
 * Format chip amounts for display.
 * 1000 → "1K", 1000000 → "1M", 1000000000 → "1B", etc.
 * Amounts under 1000 are shown as-is.
 */
export function formatChips(amount: number): string {
  if (amount >= 1_000_000_000) {
    const billions = amount / 1_000_000_000;
    return `${stripTrailingZeros(billions)}B`;
  }
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `${stripTrailingZeros(millions)}M`;
  }
  if (amount >= 1_000) {
    const thousands = amount / 1_000;
    return `${stripTrailingZeros(thousands)}K`;
  }
  return String(amount);
}

function stripTrailingZeros(value: number): string {
  // Use toFixed to handle floating point, then strip trailing zeros
  const formatted = value.toFixed(1);
  return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
}
